import React, { createContext, ReactNode, useEffect, useState } from 'react'
import CommandCenterService from '../../../service/CommandCenterService'
import MissionService from '../../../service/MissionService'
import useUrlParams from '../hooks/useUrlParams'
import useError from '../hooks/useError'
import useSocket from '../hooks/useSocket'
import useScenario from '../hooks/useScenario'
import useContacts from '../hooks/useContacts'

export type Orbit = {
    documentMetadata: any,
    groundStation: any,
    areaOfInterest: any,
    groundTarget: any,
    satToGsConnection: any,
    satToGTConnection: any,
    satellite: any
}

export type OrbitParams = {
    "epoch": number,
    "altitude": number,
    "eccentricity": number,
    "inclination": number,
    "TA": number,
    "RAAN": number,
    "AP": number,
    "semiMajorAxis": number
}

export type LaunchDetails = {
    "tle": string,
    "createdAt": string,
    "satId": string,
    "tleType": string,
    "OrbitParameters": OrbitParams
}

type OrbitContextType = {
    orbitData: Orbit | null,
    orbitParams: OrbitParams,
    launchDetails: LaunchDetails[],
    getLaunchDetails: () => void,
    loading: boolean;
    setLoading: (loading: boolean) => void
}

export const OrbitContext = createContext<OrbitContextType | undefined>(undefined);

interface OrbitProviderProps {
    children: ReactNode;
}

const OrbitProvider: React.FC<OrbitProviderProps> = ({ children }) => {

    const { sat_id, scenario_id, dashboard, truetwin_id } = useUrlParams()
    const { socket, loading, setLoading } = useSocket()
    const { set_simulation_time } = useScenario()

    const { get_time_based_gs_contact_details } = useContacts()
    const [orbitParams, setOrbitParams] = useState<OrbitParams>({} as OrbitParams);
    const [launchDetails, setLaunchDetails] = useState<LaunchDetails[]>([]);

    const [orbitData, setOrbitData] = useState<Orbit | null>(null)
    const { setError } = useError()

    const getOrbitParams = () => {
        CommandCenterService.getScenarioOrbitParams(scenario_id).then(res => {
            if (res.data) {
                setOrbitParams(res.data)
            }
        }).catch(err => {
            console.log(err)
        })
    }

    const getLaunchDetails = () => {
        MissionService.getAllOrbitDetails(sat_id).then(res => {
            if (res.data) {
                const launchDetails = res.data.sort((a: LaunchDetails, b: LaunchDetails) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                setLaunchDetails(launchDetails)
            }
        }).catch(err => {
            console.log(err)
        })
    }

    const getOrbitDetails = () => {
        const propagation = dashboard === 'summary' ? 'summary' : 'dashboard'
        CommandCenterService.getOribtData(scenario_id, truetwin_id, propagation).then(res => {
            if (res?.data) {
                setOrbitData(res?.data)
            }
        }).catch(err => {
            const error_msg = err?.message
            const error_code = error_msg === 'Network Error' ? error_msg : '500'
            // setError(error_code)
        }).finally(() => {
            setLoading(false)
        })
    }

    const getOperatedOrbitDetails = () => {
        CommandCenterService.getOperatedOrbit(scenario_id).then(res => {
            if (res?.data) {
                setOrbitData(res?.data)
                const epochTime = Math.round(new Date(res?.data?.documentMetadata?.clock?.currentTime).getTime());
                const scenario_start_time = epochTime / 1000
                const scenario_duration = 1440 * 60
                const scenario_end_time = scenario_start_time + scenario_duration
                get_time_based_gs_contact_details(scenario_id!, scenario_start_time, scenario_end_time, 'compact')
                set_simulation_time({
                    start_time: scenario_start_time,
                    end_time: scenario_end_time,
                    duration: scenario_duration
                })
            }
        }).catch(err => {
            const error_msg = err?.message
            const error_code = error_msg === 'Network Error' ? error_msg : '500'
            // setError(error_code)
        }).finally(() => {
            setLoading(false)
        })
    }

    useEffect(() => {
        if (dashboard !== 'operate' && scenario_id && truetwin_id) {
            getOrbitDetails()
        }
    }, [dashboard, scenario_id, truetwin_id])

    useEffect(() => {
        if (dashboard === 'operate' && scenario_id && socket) {
            getOperatedOrbitDetails()
            socket.onmessage = (event) => {
                try {
                    if (event.data === 'REFRESH_ORBIT_PATH') {
                        getOperatedOrbitDetails()
                    }
                } catch (error) {
                    console.error("Error parsing message:", error, event.data);
                }
            };
        }
    }, [dashboard, scenario_id, socket])

    useEffect(() => {
        if (scenario_id && dashboard !== 'operate') {
            getOrbitParams()
        } else if (sat_id && dashboard === 'operate') {
            getLaunchDetails()
        }
    }, [scenario_id, sat_id])

    return (
        <OrbitContext.Provider value={{ orbitData, orbitParams, launchDetails, loading, setLoading, getLaunchDetails }}>
            {children}
        </OrbitContext.Provider>
    )
}

export default OrbitProvider